import { useAtom } from "jotai";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";

import { cepService } from "@/app/services/cepService";
import { currentStep } from "@/app/store";
import { states } from "@/app/utils/states";
import { zipCodeMask } from "@/app/utils/zipCodeFormater";
import { BasicInput } from "@/view/components/BasicInput";
import { BasicSelect } from "@/view/components/BasicSelect";
import CityInput from "@/view/components/CrefazClt/CityInput";
import { Button } from "@/view/components/ui/button";

interface AddressFormProps {
  visible?: boolean;
}

export default function AddressForm({ visible }: AddressFormProps) {
  const [, setStep] = useAtom(currentStep);

  const {
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext();

  const cep = watch("endereco.cep")?.replace(/\D/g, "");
  const uf = watch("endereco.uf");

  useEffect(() => {
    if (cep?.length !== 8) return;

    const fetchAddress = async () => {
      try {
        const address = await cepService(cep);

        if (address?.erro) return;

        setValue("endereco.logradouro", address.logradouro);
        setValue("endereco.bairro", address.bairro);
        setValue("endereco.cidade", address.localidade);
        setValue("endereco.uf", address.uf);
      } catch {
        // endereço preenchido manualmente
      }
    };

    fetchAddress();
  }, [cep, setValue]);

  const enderecoErrors = (errors.endereco ?? {}) as Record<
    string,
    { message?: string }
  >;

  const handleNext = async () => {
    const isValid = await trigger("endereco");

    if (isValid) setStep((state) => state + 1);
  };

  return (
    <div className={visible ? "flex flex-col gap-8" : "hidden"}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <BasicInput
          name="endereco.cep"
          label="CEP"
          onInput={zipCodeMask}
          maxLength={9}
          error={enderecoErrors.cep?.message}
        />
        <BasicInput
          name="endereco.logradouro"
          label="Logradouro"
          error={enderecoErrors.logradouro?.message}
        />
        <BasicInput
          name="endereco.numero"
          label="Número"
          error={enderecoErrors.numero?.message}
        />
        <BasicInput
          name="endereco.complemento"
          label="Complemento (opcional)"
          error={enderecoErrors.complemento?.message}
        />
        <BasicInput
          name="endereco.bairro"
          label="Bairro"
          error={enderecoErrors.bairro?.message}
        />
        <BasicSelect
          name="endereco.uf"
          label="UF"
          options={states}
          placeholder="Selecione"
          error={enderecoErrors.uf?.message}
        />
        <CityInput
          name="endereco.cidade"
          idName="endereco.cidadeId"
          label="Cidade"
          uf={uf}
          error={
            enderecoErrors.cidade?.message ?? enderecoErrors.cidadeId?.message
          }
        />
      </div>

      <div className="flex justify-end gap-5">
        <Button
          type="button"
          className="rounded-full bg-white02 px-8 py-3 font-semibold text-primary hover:bg-white02"
          onClick={() => setStep((state) => state - 1)}
        >
          Voltar
        </Button>
        <Button
          type="button"
          className="rounded-full px-8 py-3 font-semibold text-white"
          onClick={handleNext}
        >
          Continuar
        </Button>
      </div>
    </div>
  );
}
